import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import { isOAuthConfigured } from '@/config/amplify';
import { useAuthStore } from '@/stores/authStore';

import AppleSignInButton from './components/AppleSignInButton';
import GoogleSignInButton from './components/GoogleSignInButton';

export default function LoginPage() {
  const [isSigningIn, setIsSigningIn] = useState(false);
  const { isAuthenticated, error, loginWithApple, loginWithGoogle, clearError } = useAuthStore();
  const location = useLocation();
  const oauthConfigured = isOAuthConfigured();

  // Clear any stale errors from other auth pages (e.g. registration error
  // persisting after navigating back to login)
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Reset signing-in state when page is restored from bfcache (user pressed back
  // during OAuth redirect without completing sign-in)
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setIsSigningIn(false);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Get the redirect destination
  const from = (location.state as { from?: Location })?.from?.pathname || '/';

  // Redirect if already authenticated (checked FIRST - handles successful sign-in redirect)
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  // Show loading screen during active sign-in (this check is AFTER isAuthenticated,
  // so successful sign-in will redirect above before showing this spinner)
  if (isSigningIn) {
    return (
      <main className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4" />
          <p className="text-purple-300 text-lg">Signing you in...</p>
        </div>
      </main>
    );
  }

  const handleAppleSignIn = async () => {
    clearError();
    setIsSigningIn(true);
    try {
      // This will redirect to Cognito/Apple - page will unload
      await loginWithApple();
    } catch (_error) {
      setIsSigningIn(false);
    }
  };

  const handleGoogleSignIn = async () => {
    clearError();
    setIsSigningIn(true);
    try {
      // This will redirect to Cognito/Google - page will unload
      await loginWithGoogle();
    } catch (_error) {
      setIsSigningIn(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Yarn Tales</h1>
          <p className="text-gray-400">Branching Fiction, Woven Together</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Auth Buttons */}
        <div className="space-y-3 mb-6">
          {oauthConfigured && (
            <>
              <AppleSignInButton onClick={handleAppleSignIn} />
              <GoogleSignInButton onClick={handleGoogleSignIn} />
            </>
          )}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-gray-700" />
          <span className="text-gray-400 text-sm">or</span>
          <div className="flex-1 h-px bg-gray-700" />
        </div>

        {/* Email Sign In */}
        <Link
          to="/email-login"
          className="block w-full text-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          Sign in with email
        </Link>

        {/* Sign Up Link */}
        <p className="text-center mt-6 text-gray-400">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="text-purple-400 hover:text-purple-300 underline">
            Sign up
          </Link>
        </p>

        {/* Support Link */}
        <p className={`text-center mt-4 ${error ? 'text-gray-300' : 'text-gray-400'} text-sm`}>
          Need help?{' '}
          <a
            href="mailto:support@yarntales.io"
            className={`${error ? 'text-purple-400 hover:text-purple-300' : 'text-gray-400 hover:text-gray-300'} transition-colors`}
          >
            Contact support
          </a>
        </p>
      </div>
    </main>
  );
}
